import { useEffect, useState } from 'react';
import {
  listTrustedProxies,
  createTrustedProxy,
  deleteTrustedProxy,
  type TrustedProxy,
} from '../../services/trustedProxies';

export function TrustedProxies() {
  const [rows, setRows] = useState<TrustedProxy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const resp = await listTrustedProxies();
      setRows(resp.data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function onCreate() {
    if (!cidr || !label) {
      setError('cidr + label required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createTrustedProxy({ cidr, label, notes: notes || undefined });
      setCidr(''); setLabel(''); setNotes('');
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(id: number) {
    if (!confirm('Delete trusted proxy permanently?')) return;
    try {
      await deleteTrustedProxy(id);
      await refresh();
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Trusted Proxies (XFF whitelist)</h1>

      <div className="bg-amber-900/30 p-3 rounded border border-amber-700 text-sm">
        ⚠️ Operadores: estes CIDRs são confiados pra ler X-Forwarded-For do client.{' '}
        Sem populated, setting <code>xff.use_for_real_ip</code> não tem efeito (failsafe §19.14).{' '}
        Tipico setup: Traefik docker network + Cloudflare ranges.
      </div>

      <div className="bg-gray-900 p-4 rounded border border-gray-800">
        <h2 className="text-sm uppercase text-gray-400 mb-2">Add new CIDR</h2>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="block text-xs text-gray-400">CIDR (IPv4)</label>
            <input type="text" value={cidr} onChange={(e) => setCidr(e.target.value)}
              placeholder="172.19.0.0/16"
              className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-44" />
          </div>
          <div>
            <label className="block text-xs text-gray-400">Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="traefik docker network"
              className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-56" />
          </div>
          <div className="flex-grow">
            <label className="block text-xs text-gray-400">Notes (opcional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-full" />
          </div>
          <button onClick={onCreate} disabled={creating}
            className="px-4 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white">
            {creating ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      {error && <div className="text-red-400">Error: {error}</div>}

      <div>
        <h2 className="text-sm uppercase text-gray-400 mb-2">Current CIDRs ({rows.length})</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-800">
            <thead className="bg-gray-900 text-gray-300">
              <tr>
                <th className="text-left p-2">CIDR</th>
                <th className="text-left p-2">Label</th>
                <th className="text-left p-2">Enabled</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Notes</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-800">
                  <td className="p-2 font-mono">{r.cidr}</td>
                  <td className="p-2">{r.label}</td>
                  <td className="p-2">{r.enabled ? '✓' : '✗'}</td>
                  <td className="p-2 text-xs">{r.created_at}</td>
                  <td className="p-2 text-xs text-gray-400">{r.notes || '—'}</td>
                  <td className="p-2">
                    <button onClick={() => onDelete(r.id)}
                      className="text-red-400 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-400">
                  Sem trusted proxies. XFF será ignorado.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TrustedProxies;
